import React, { useState } from 'react';
import { Building, TrendingUp, DollarSign, Calculator, Calendar, BarChart3 } from 'lucide-react';
import AdSense from '../components/AdSense';
import { useSEO } from '../hooks/useSEO';

const FIIs: React.FC = () => {
  useSEO({
    title: 'Fundos Imobiliários (FIIs) 2026: Guia e Calculadora de Dividendos | O Teu Dinheiro',
    description: 'Aprenda a viver de renda com Fundos Imobiliários (FIIs). Simule dividendos mensais isentos de Imposto de Renda e entenda fundos de tijolo e papel.',
    keywords: 'fundos imobiliários 2026, FIIs dividendos, calculadora de FIIs, fundos de tijolo, fundos de papel, renda passiva mensal',
    url: 'https://www.oteudinheiro.com.br/fiis'
  });
  const [valor, setValor] = useState('');
  const [dividendo, setDividendo] = useState('');
  const [preco, setPreco] = useState('');

  const calcularDividendo = () => {
    if (!valor || !dividendo || !preco) return null;
    
    const valorNum = parseFloat(valor);
    const dividendoNum = parseFloat(dividendo);
    const precoNum = parseFloat(preco);
    
    const cotas = valorNum / precoNum;
    const rendimentoMensal = cotas * dividendoNum;
    const rendimentoAnual = rendimentoMensal * 12;
    const rentabilidade = (rendimentoAnual / valorNum) * 100;
    
    return {
      cotas: cotas.toFixed(2),
      rendimentoMensal: rendimentoMensal.toFixed(2),
      rendimentoAnual: rendimentoAnual.toFixed(2),
      rentabilidade: rentabilidade.toFixed(2)
    };
  };

  const resultado = calcularDividendo();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 to-purple-700 text-white py-12 sm:py-16 md:py-20">
        <div className="container-custom text-center px-4">
          <Building className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 mx-auto mb-4 sm:mb-6" />
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight">
            Fundos Imobiliários (FIIs)
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto leading-relaxed">
            Ganhe com aluguel sem precisar comprar imóvel
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom px-4">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
                O que são Fundos Imobiliários?
              </h2>
              
              <div className="prose prose-lg text-gray-600 mb-8">
                <p className="mb-4">
                  FIIs são fundos que investem em imóveis e distribuem a maior parte dos lucros 
                  aos investidores na forma de dividendos mensais.
                </p>
                <p className="mb-4">
                  É como ser sócio de um condomínio comercial, mas sem precisar se preocupar com 
                  manutenção, inquilinos ou burocracia.
                </p>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                Tipos de FIIs
              </h3>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-purple-500">
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                    <Building className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mr-2 flex-shrink-0" />
                    FIIs de Tijolo
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    Investem em imóveis físicos como shoppings, escritórios, galpões logísticos.
                  </p>
                  <ul className="text-xs sm:text-sm text-gray-500 space-y-1">
                    <li>• Imóveis reais</li>
                    <li>• Aluguel mensal</li>
                    <li>• Valorização do imóvel</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-blue-500">
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 flex-shrink-0" />
                    FIIs de Papel
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    Investem em títulos de dívida imobiliária, CRIs e outros papéis.
                  </p>
                  <ul className="text-xs sm:text-sm text-gray-500 space-y-1">
                    <li>• Títulos de dívida</li>
                    <li>• Maior liquidez</li>
                    <li>• Menor volatilidade</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-green-500">
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                    <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2 flex-shrink-0" />
                    FIIs Híbridos
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    Combinam imóveis físicos e títulos de dívida imobiliária.
                  </p>
                  <ul className="text-xs sm:text-sm text-gray-500 space-y-1">
                    <li>• Diversificação</li>
                    <li>• Equilíbrio risco/retorno</li>
                    <li>• Menor volatilidade</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-orange-500">
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 mr-2 flex-shrink-0" />
                    Fundos de Fundos (FoFs)
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    Investem em cotas de outros FIIs, proporcionando diversificação automática.
                  </p>
                  <ul className="text-xs sm:text-sm text-gray-500 space-y-1">
                    <li>• Diversificação automática</li>
                    <li>• Gestão profissional</li>
                    <li>• Menor risco específico</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-red-500">
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                    <Building className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2 flex-shrink-0" />
                    FIIs de Desenvolvimento
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    Focados na construção e desenvolvimento de novos projetos imobiliários.
                  </p>
                  <ul className="text-xs sm:text-sm text-gray-500 space-y-1">
                    <li>• Maior potencial de retorno</li>
                    <li>• Maior risco</li>
                    <li>• Longo prazo</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-l-4 border-yellow-500">
                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-2 flex-shrink-0" />
                    Fiagros
                  </h4>
                  <p className="text-sm sm:text-base text-gray-600 mb-2 sm:mb-3">
                    Fundos de Investimento nas Cadeias Produtivas Agroindustriais.
                  </p>
                  <ul className="text-xs sm:text-sm text-gray-500 space-y-1">
                    <li>• Setor agropecuário</li>
                    <li>• CRAs e propriedades rurais</li>
                    <li>• Diversificação setorial</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                Vantagens dos FIIs
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className="bg-purple-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Renda mensal</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">Dividendos pagos mensalmente</p>
                  </div>
                </div>

                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className="bg-blue-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                    <Building className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Sem burocracia</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">Não precisa gerenciar imóveis</p>
                  </div>
                </div>

                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className="bg-green-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Diversificação</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">Acesso a vários tipos de imóveis</p>
                  </div>
                </div>

                <div className="flex items-start space-x-2 sm:space-x-3">
                  <div className="bg-orange-100 p-1.5 sm:p-2 rounded-lg flex-shrink-0">
                    <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">Baixo investimento</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">A partir de R$ 10,00</p>
                  </div>
                </div>
              </div>

              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                Comparação dos Tipos de FIIs
              </h3>
              
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-6 sm:mb-8">
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="w-full text-xs sm:text-sm min-w-[600px]">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Tipo</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Risco</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Liquidez</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Rendimento</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Perfil</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 font-medium">FIIs de Tijolo</td>
                        <td className="py-3 px-4">Médio</td>
                        <td className="py-3 px-4">Alta</td>
                        <td className="py-3 px-4">Estável</td>
                        <td className="py-3 px-4">Conservador</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 font-medium">FIIs de Papel</td>
                        <td className="py-3 px-4">Baixo</td>
                        <td className="py-3 px-4">Muito Alta</td>
                        <td className="py-3 px-4">Previsível</td>
                        <td className="py-3 px-4">Conservador</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 font-medium">FIIs Híbridos</td>
                        <td className="py-3 px-4">Médio</td>
                        <td className="py-3 px-4">Alta</td>
                        <td className="py-3 px-4">Equilibrado</td>
                        <td className="py-3 px-4">Moderado</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 font-medium">Fundos de Fundos</td>
                        <td className="py-3 px-4">Baixo-Médio</td>
                        <td className="py-3 px-4">Alta</td>
                        <td className="py-3 px-4">Diversificado</td>
                        <td className="py-3 px-4">Moderado</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-3 px-4 font-medium">FIIs de Desenvolvimento</td>
                        <td className="py-3 px-4">Alto</td>
                        <td className="py-3 px-4">Baixa</td>
                        <td className="py-3 px-4">Variável</td>
                        <td className="py-3 px-4">Agressivo</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium">Fiagros</td>
                        <td className="py-3 px-4">Médio-Alto</td>
                        <td className="py-3 px-4">Média</td>
                        <td className="py-3 px-4">Sazonal</td>
                        <td className="py-3 px-4">Moderado-Agressivo</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Right Column - Calculator */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 lg:sticky lg:top-24">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">
                  Calculadora de Dividendos
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      Valor a investir (R$)
                    </label>
                    <input
                      type="number"
                      value={valor}
                      onChange={(e) => setValor(e.target.value)}
                      className="w-full px-2 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="1000"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      Preço da cota (R$)
                    </label>
                    <input
                      type="number"
                      value={preco}
                      onChange={(e) => setPreco(e.target.value)}
                      className="w-full px-2 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="100"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                      Dividendo por cota (R$)
                    </label>
                    <input
                      type="number"
                      value={dividendo}
                      onChange={(e) => setDividendo(e.target.value)}
                      className="w-full px-2 sm:px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="0.80"
                      step="0.01"
                    />
                  </div>

                  {resultado && (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                      <div className="text-center">
                        <p className="text-xs sm:text-sm text-purple-600 mb-1">Resultados:</p>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 text-xs sm:text-sm">
                        <div className="bg-white p-1.5 sm:p-2 rounded">
                          <p className="text-purple-600 font-medium text-xs sm:text-sm">Cotas:</p>
                          <p className="text-purple-700 font-bold text-sm sm:text-base">{resultado.cotas}</p>
                        </div>
                        <div className="bg-white p-1.5 sm:p-2 rounded">
                          <p className="text-purple-600 font-medium text-xs sm:text-sm">Renda Mensal:</p>
                          <p className="text-purple-700 font-bold text-sm sm:text-base">R$ {resultado.rendimentoMensal}</p>
                        </div>
                        <div className="bg-white p-1.5 sm:p-2 rounded">
                          <p className="text-purple-600 font-medium text-xs sm:text-sm">Renda Anual:</p>
                          <p className="text-purple-700 font-bold text-sm sm:text-base">R$ {resultado.rendimentoAnual}</p>
                        </div>
                        <div className="bg-white p-1.5 sm:p-2 rounded">
                          <p className="text-purple-600 font-medium text-xs sm:text-sm">Rentabilidade:</p>
                          <p className="text-purple-700 font-bold text-sm sm:text-base">{resultado.rentabilidade}%</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Section */}
      <section className="py-8">
        <div className="container-custom px-4">
          <AdSense />
        </div>
      </section>

      {/* Other Fund Types Section */}
      <section className="section-padding bg-white">
        <div className="container-custom px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">
            Outros tipos de fundos de investimento
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="bg-blue-50 rounded-xl p-4 sm:p-6 border border-blue-200">
              <h3 className="text-lg sm:text-xl font-semibold text-blue-900 mb-2 sm:mb-3 flex items-center">
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 flex-shrink-0" />
                FIDCs
              </h3>
              <p className="text-sm sm:text-base text-blue-700 mb-2 sm:mb-3">
                Fundos de Investimento em Direitos Creditórios. Investem em recebíveis e títulos de crédito.
              </p>
              <ul className="text-xs sm:text-sm text-blue-600 space-y-1">
                <li>• Maior risco</li>
                <li>• Maior rentabilidade</li>
                <li>• Liquidez variável</li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-xl p-4 sm:p-6 border border-green-200">
              <h3 className="text-lg sm:text-xl font-semibold text-green-900 mb-2 sm:mb-3 flex items-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2 flex-shrink-0" />
                FIPs
              </h3>
              <p className="text-sm sm:text-base text-green-700 mb-2 sm:mb-3">
                Fundos de Investimento em Participações. Investem em empresas não listadas.
              </p>
              <ul className="text-xs sm:text-sm text-green-600 space-y-1">
                <li>• Private equity</li>
                <li>• Longo prazo</li>
                <li>• Alto potencial</li>
              </ul>
            </div>

            <div className="bg-purple-50 rounded-xl p-4 sm:p-6 border border-purple-200">
              <h3 className="text-lg sm:text-xl font-semibold text-purple-900 mb-2 sm:mb-3 flex items-center">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mr-2 flex-shrink-0" />
                FIAs
              </h3>
              <p className="text-sm sm:text-base text-purple-700 mb-2 sm:mb-3">
                Fundos de Investimento em Ações. Investem em ações de empresas listadas.
              </p>
              <ul className="text-xs sm:text-sm text-purple-600 space-y-1">
                <li>• Renda variável</li>
                <li>• Gestão profissional</li>
                <li>• Diversificação</li>
              </ul>
            </div>

            <div className="bg-orange-50 rounded-xl p-4 sm:p-6 border border-orange-200">
              <h3 className="text-lg sm:text-xl font-semibold text-orange-900 mb-2 sm:mb-3 flex items-center">
                <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 mr-2 flex-shrink-0" />
                FICs
              </h3>
              <p className="text-sm sm:text-base text-orange-700 mb-2 sm:mb-3">
                Fundos de Investimento em Cotas. Investem em cotas de outros fundos.
              </p>
              <ul className="text-xs sm:text-sm text-orange-600 space-y-1">
                <li>• Diversificação</li>
                <li>• Gestão terceirizada</li>
                <li>• Menor controle</li>
              </ul>
            </div>

            <div className="bg-red-50 rounded-xl p-4 sm:p-6 border border-red-200">
              <h3 className="text-lg sm:text-xl font-semibold text-red-900 mb-2 sm:mb-3 flex items-center">
                <Building className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2 flex-shrink-0" />
                FIMs
              </h3>
              <p className="text-sm sm:text-base text-red-700 mb-2 sm:mb-3">
                Fundos de Investimento Multimercado. Investem em diferentes classes de ativos.
              </p>
              <ul className="text-xs sm:text-sm text-red-600 space-y-1">
                <li>• Estratégias complexas</li>
                <li>• Maior flexibilidade</li>
                <li>• Risco variável</li>
              </ul>
            </div>

            <div className="bg-yellow-50 rounded-xl p-4 sm:p-6 border border-yellow-200">
              <h3 className="text-lg sm:text-xl font-semibold text-yellow-900 mb-2 sm:mb-3 flex items-center">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-2 flex-shrink-0" />
                Fundos de Renda Fixa
              </h3>
              <p className="text-sm sm:text-base text-yellow-700 mb-2 sm:mb-3">
                Investem em títulos de renda fixa como CDBs, LCIs, LCAs e Tesouro Direto.
              </p>
              <ul className="text-xs sm:text-sm text-yellow-600 space-y-1">
                <li>• Baixo risco</li>
                <li>• Rentabilidade previsível</li>
                <li>• Alta liquidez</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How to Invest Section */}
      <section className="section-padding">
        <div className="container-custom px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">
            Como investir em FIIs
          </h2>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            <div className="text-center">
              <div className="bg-purple-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-lg sm:text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                Abra uma conta
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Crie conta em uma corretora que opere na B3.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-lg sm:text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                Pesquise FIIs
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Analise histórico de dividendos e qualidade dos ativos.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-lg sm:text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                Compre as cotas
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Execute a ordem de compra pelo home broker.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-purple-100 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <span className="text-lg sm:text-2xl font-bold text-purple-600">4</span>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-3">
                Receba dividendos
              </h3>
              <p className="text-sm sm:text-base text-gray-600">
                Os dividendos são creditados automaticamente na sua conta.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding">
        <div className="container-custom px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8 sm:mb-12">
            Dicas para investir em FIIs
          </h2>
          
          <div className="grid sm:grid-cols-2 gap-6 sm:gap-8">
            <div className="tip-box">
              <h3 className="text-lg sm:text-xl font-semibold text-secondary-900 mb-2 sm:mb-3">
                Diversifique
              </h3>
              <p className="text-sm sm:text-base text-secondary-700">
                Não coloque todo seu dinheiro em um só FII. Distribua entre diferentes 
                tipos e setores imobiliários.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-lg sm:text-xl font-semibold text-accent-900 mb-2 sm:mb-3">
                Cuidado com a volatilidade
              </h3>
              <p className="text-sm sm:text-base text-accent-700">
                FIIs podem ter variação de preço. Foque nos dividendos mensais 
                e pense no longo prazo.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-lg sm:text-xl font-semibold text-secondary-900 mb-2 sm:mb-3">
                Analise o histórico
              </h3>
              <p className="text-sm sm:text-base text-secondary-700">
                Verifique se o FII paga dividendos regularmente e se mantém 
                uma boa rentabilidade ao longo do tempo.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-lg sm:text-xl font-semibold text-accent-900 mb-2 sm:mb-3">
                Impostos
              </h3>
              <p className="text-sm sm:text-base text-accent-700">
                Dividendos de FIIs são isentos de IR, mas você paga IOF e 
                IR sobre ganho de capital na venda.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-lg sm:text-xl font-semibold text-secondary-900 mb-2 sm:mb-3">
                Escolha o tipo certo
              </h3>
              <p className="text-sm sm:text-base text-secondary-700">
                Para iniciantes, prefira FIIs de Papel ou Híbridos. Para mais experiência, 
                considere FIIs de Tijolo ou Fundos de Fundos.
              </p>
            </div>

            <div className="warning-box">
              <h3 className="text-lg sm:text-xl font-semibold text-accent-900 mb-2 sm:mb-3">
                Cuidado com FIIs de Desenvolvimento
              </h3>
              <p className="text-sm sm:text-base text-accent-700">
                São mais arriscados e podem não pagar dividendos durante a obra. 
                Só invista se entender os riscos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Section */}
      <section className="py-8 bg-gray-50">
        <div className="container-custom px-4">
          <AdSense />
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-purple-600 text-white">
        <div className="container-custom text-center px-4">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
            Comece a investir em FIIs hoje!
          </h2>
          <p className="text-lg sm:text-xl text-purple-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Construa uma renda passiva mensal com imóveis sem precisar comprar um.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a
              href="/calculadoras"
              className="btn bg-white text-purple-600 hover:bg-gray-100 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
            >
              Usar Calculadoras
            </a>
            <a
              href="/investimentos"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-purple-600 text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3"
            >
              Ver outros investimentos
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FIIs;
